import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { BasketDetails } from 'shared-types/Basket';
import { Property } from 'shared-types/Property';
import { updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';
import { removeBasketItem } from '../utils/removeBasketItem';

type AppState = { basket: { basketDetails: BasketDetails } } & Record<string, unknown>;

type Removal = {
  id: string;
  startedAt: number;
  basketDetails: BasketDetails;
};

const MIN_SPINNER_VISIBLE_MS = 700;

const MAX_SPINNER_VISIBLE_MS = 30000;

export const useRemoveBasketItem = (language: string) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, AnyAction>>();
  const property = useSelector(entitySelectors.getProperty) as Property;
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as BasketDetails;
  const isFetching = useSelector(basketSelectors.getIsBasketDetailsFetching) as boolean;
  const [removal, setRemoval] = useState<Removal | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!removal) return undefined;
    const elapsed = Date.now() - removal.startedAt;
    const deadline = isFetching ? MAX_SPINNER_VISIBLE_MS : MIN_SPINNER_VISIBLE_MS;
    timer.current = setTimeout(() => setRemoval(null), Math.max(0, deadline - elapsed));
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [removal, isFetching]);

  const removeItem = useCallback(
    (id: string) => {
      if (!basketDetails || !property) return;
      if (!Array.isArray(basketDetails.items)) return;
      const remaining = removeBasketItem(basketDetails.items, id);
      setRemoval({ id, startedAt: Date.now(), basketDetails });
      dispatch(
        updateBasket(
          property.id,
          basketDetails.id,
          language,
          { promoCode: basketDetails.promoCode, items: remaining },
          true
        )
      );
    },
    [basketDetails, property, language, dispatch]
  );

  return {
    removeItem,
    removingItemId: removal?.id ?? null,
    isUpdating: removal !== null,
    displayBasketDetails: removal?.basketDetails ?? basketDetails,
  };
};
